import React from 'react';
import Badge from '../../../components/Atoms/Badge';
import { useI18n } from '../../../i18n';
import { typeKey, versionLabel } from '../utils/predictionView';

/**
 * The model picker: every TRAINED version, grouped project → model.
 *
 * Only completed versions reach this list — the API refuses to offer anything
 * else, because without a finished run the artifact resolver falls back to the
 * base weights and the model would answer with COCO classes the user never
 * trained. A version that IS trained but whose checkpoint is not on this
 * server's disk is shown greyed with its reason rather than hidden: "you never
 * trained this" and "this server cannot reach what you trained" need different
 * actions from the user.
 */

const TYPE_VARIANT = {
  classification: 'info',
  detection: 'warning',
  segmentation: 'primary',
};

const EmptyState = ({ icon, title, hint }) => (
  <div className="lf-predict-empty">
    <i className={`fas ${icon}`} aria-hidden="true"></i>
    <p className="lf-predict-empty__title">{title}</p>
    {hint && <p className="lf-predict-empty__hint">{hint}</p>}
  </div>
);

const ModelList = ({ grouped, catalogue, loading, error, search, selectedVersionId, onSearch, onSelect, onRefresh }) => {
  const { t } = useI18n();

  const header = (
    <div className="lf-predict-panel__head">
      <h2 className="lf-predict-panel__title">{t('prediction.models.title')}</h2>
      <button type="button" className="lf-predict-icon-btn" onClick={onRefresh} title={t('common.refresh')}>
        <i className="fas fa-sync" aria-hidden="true"></i>
        <span className="lf-sr-only">{t('common.refresh')}</span>
      </button>
    </div>
  );

  let body;
  if (loading && !catalogue) {
    body = <EmptyState icon="fa-spinner fa-spin" title={t('prediction.models.loading')} />;
  } else if (error === 'noOwner') {
    body = <EmptyState icon="fa-user-slash" title={t('prediction.models.noOwner')} hint={t('prediction.models.noOwnerHint')} />;
  } else if (error) {
    body = <EmptyState icon="fa-exclamation-triangle" title={t('prediction.models.error')} hint={error} />;
  } else if (!catalogue?.trained) {
    // Two different nothings. `total_versions` counts every version the user
    // has, trained or not — so "you have models but none finished training" is
    // a different sentence, and a different next step, from "you have no
    // models". Collapsing them is how a screen sends someone to the wrong page.
    body = catalogue?.total_versions ? (
      <EmptyState icon="fa-hourglass-half" title={t('prediction.models.noneTrained')} hint={t('prediction.models.noneTrainedHint')} />
    ) : (
      <EmptyState icon="fa-cube" title={t('prediction.models.noModels')} hint={t('prediction.models.noModelsHint')} />
    );
  } else if (!grouped.length) {
    body = <EmptyState icon="fa-search" title={t('prediction.models.noMatch')} hint={t('prediction.models.noMatchHint')} />;
  } else {
    body = (
      <div className="lf-predict-tree">
        {grouped.map((project) => (
          <section key={project.project_id || project.project_name} className="lf-predict-tree__project">
            <h3 className="lf-predict-tree__projectName">
              <i className="fas fa-folder-open" aria-hidden="true"></i> {project.project_name}
            </h3>
            {project.models.map((model) => (
              <div key={model.model_id} className="lf-predict-tree__model">
                <div className="lf-predict-tree__modelHead">
                  <span className="lf-predict-tree__modelName">{model.model_name}</span>
                  <Badge variant={TYPE_VARIANT[model.model_type] || 'neutral'}>
                    {t(`prediction.type.${typeKey(model.model_type)}`)}
                  </Badge>
                </div>
                <ul className="lf-predict-tree__versions">
                  {model.versions.map((entry) => {
                    const isSelected = entry.id === selectedVersionId;
                    return (
                      <li key={entry.id}>
                        <button
                          type="button"
                          className={`lf-predict-version${isSelected ? ' is-selected' : ''}${
                            entry.runnable ? '' : ' is-unavailable'
                          }`}
                          onClick={() => onSelect(entry)}
                          disabled={!entry.runnable}
                          aria-pressed={isSelected}
                        >
                          <span className="lf-predict-version__row">
                            <span className="lf-predict-version__name">{versionLabel(entry)}</span>
                            {entry.runnable ? (
                              <span className="lf-predict-version__count">
                                {t('prediction.models.classCount', { count: entry.classes?.length || 0 })}
                              </span>
                            ) : (
                              <Badge variant="danger">{t('prediction.models.unavailable')}</Badge>
                            )}
                          </span>
                          {entry.runnable ? (
                            <span className="lf-predict-version__classes">
                              {(entry.classes || []).slice(0, 4).map((name) => (
                                <span key={name} className="lf-predict-chip">
                                  {name}
                                </span>
                              ))}
                              {(entry.classes || []).length > 4 && (
                                <span className="lf-predict-chip lf-predict-chip--more">
                                  +{entry.classes.length - 4}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="lf-predict-version__reason">{entry.reason}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="lf-predict-panel">
      {header}
      {Boolean(catalogue?.trained) && (
        <label className="lf-predict-search">
          <i className="fas fa-search" aria-hidden="true"></i>
          <input
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={t('prediction.models.searchPlaceholder')}
            aria-label={t('prediction.models.searchPlaceholder')}
          />
        </label>
      )}
      <div className="lf-predict-panel__body">{body}</div>
    </div>
  );
};

export default ModelList;
